import _, { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ToastComponent from '../components/toast';
import { selectToken } from '../pages/auth/authSlice';
import { type ApiError, OpenAPI } from '@wealthlane/api-services/wealthlane-customer-service';
import { selectTenantId } from '../pages/configuration/customerSwitchUserSlice';
import { useNavigate } from 'react-router-dom';
import useInstance from './custom-hooks/useInstance';
import { attachDecimalConfigToRequest } from '../utils/apiDecimalConfig';
import { showErrorModal } from '../components/error-modal/errorModalEmitter';

export function useWealthlaneCustomerApi(): {
  dismissError: () => void;
  error: ApiError | undefined;
  isLoading: boolean;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
} {
  const token = useSelector(selectToken);
  const { getTenantById, getRoleId } = useInstance();
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const OtpToken: any = sessionStorage.getItem('OtpToken');

  OpenAPI.BASE = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
  OpenAPI.TOKEN = token?.toString();
  // OpenAPI.HEADERS = {
  //   [`X-Tenant-Id`]: tenantId === null ? '' : (tenantId as string),
  //   ['X-Switch-Source-Role-Id']: window.localStorage.getItem('x-switch-role-id') ?? '',
  // };
  OpenAPI.HEADERS = {
    [`x-multipurpose-token`]: OtpToken,
    [`X-Tenant-Id`]: getTenantById()?.tenantId ?? '',
    ['X-Switch-Source-Role-Id']: getRoleId() ?? '',
  };

  const handleRequest = useCallback(async function <T>(request: Promise<T>) {
    // debugger;

    setIsLoading(true);

    // Transfer decimal config metadata from promise to axios interceptor
    attachDecimalConfigToRequest(request);

    try {
      const response = await request;
      setError(undefined);
      return response;
    } catch (exception: any) {
      setError(exception);

      if (exception.status === 404 && window.location.hash.includes('cid=')) {
        throw Error(exception);
      }

      const is404Found = exception.status === 404;
      if (exception?.body?.error?.validationErrors != null) {
        const error = exception?.body?.error?.validationErrors?.map((error: any) => {
          return error.message;
        });
        if (
          error.includes(
            `OTP is invalid. Click 'Resend OTP' if you wish to generate a new code.`
          ) === true ||
          error.includes('Invalid Template') === true ||
          error.includes('Only excel file can be uploaded') === true
        ) {
        } else {
          toast.error(<ToastComponent label='Error' message={`${String(error)}`} />, {
            toastId: 1,
            autoClose: 30000,
          });
        }
      } else if (
        exception?.body?.error?.message.includes(
          `OTP is invalid. Click 'Resend OTP' if you wish to generate a new code.`
        ) === true ||
        exception?.body?.error?.message.includes('Index was outside the bounds of the array.') ===
          true ||
        exception?.body?.error?.message.includes('Only excel file can be uploaded') === true ||
        exception?.body?.error?.message.includes('Invalid Template') === true
      ) {
        //
      } else if (exception?.body?.error != null && !is404Found) {
        toast.error(
          <ToastComponent
            label='Error'
            message={`${String(exception?.body?.error?.message ?? '')}`}
          />,
          { autoClose: 30000 }
        );
      } else if (
        exception.status === 403 &&
        (isEmpty(exception?.body) || exception?.body?.error == null)
      ) {
        // ABP often returns 403 with top-level { message } and no nested body.error. Do not throw
        // (avoids check-user logout) or navigate to server-maintainance (HTF-227 / locked sandbox).
        return undefined;
      } else if (exception.status === 401) {
        //
      } else if (!_.isEmpty(exception?.body?.error?.message)) {
        toast.error(
          <ToastComponent
            label='Error'
            message={`${String(exception?.body?.error?.message ?? '')}`}
          />,
          {
            autoClose: 30000,
          }
        );
      } else {
        // toast.error(
        //   <ToastComponent
        //     label='Error'
        //     message={'Server is down at the moment. Please try again after sometime.'}
        //   />,
        //   {
        //     toastId: 2,
        //     autoClose: 30000,
        //   }
        // );
        showErrorModal({ status: exception?.status });
      }
      exception = is404Found ? 'Not Found' : exception;
      throw Error(exception);
    } finally {
      setIsLoading(false);
    }
  }, []);

  function dismissError(): void {
    setError(undefined);
  }

  return { dismissError, error, isLoading, handleRequest };
}

export default useWealthlaneCustomerApi;
